import { Particle, ParticleContainer, type Texture } from 'pixi.js';
import type { BulletPool } from '../sim/bullets/BulletPool';
import { kindRotates } from '../sim/bullets/bulletKinds';
import { lerp } from '../core/math';

/**
 * Syncs a SoA pool into a ParticleContainer (one draw call for the whole pool).
 * All capacity particles are created once at boot; unused slots are hidden with
 * scale 0 (degenerate quads cost ~nothing). Positions are interpolated between
 * the previous and current sim step for smooth high-Hz rendering.
 */
export class BulletRenderer {
  readonly container: ParticleContainer;
  private particles: Particle[] = [];
  private lastShown = 0;

  constructor(
    private readonly pool: BulletPool,
    private readonly frames: Texture[],
    private readonly fadeByAge = false,
  ) {
    this.container = new ParticleContainer({
      dynamicProperties: { position: true, rotation: true, vertex: true, uvs: true, color: true },
    });
    const fallback = frames[0]!;
    for (let i = 0; i < pool.capacity; i++) {
      const p = new Particle({ texture: fallback, x: 0, y: 0, anchorX: 0.5, anchorY: 0.5 });
      p.scaleX = 0;
      p.scaleY = 0;
      this.particles.push(p);
      this.container.addParticle(p);
    }
  }

  sync(alpha: number): void {
    const pool = this.pool;
    const n = pool.count;
    for (let i = 0; i < n; i++) {
      const p = this.particles[i]!;
      p.x = lerp(pool.px[i]!, pool.x[i]!, alpha);
      p.y = lerp(pool.py[i]!, pool.y[i]!, alpha);
      const kind = pool.kind[i]!;
      p.texture = this.frames[kind]!;
      const s = pool.scale[i]!;
      p.scaleX = s;
      p.scaleY = s;
      p.rotation = kindRotates(kind) ? Math.atan2(pool.vy[i]!, pool.vx[i]!) : 0;
      p.alpha = this.fadeByAge ? Math.max(0, 1 - pool.age[i]! / (pool.life[i]! || 1)) : 1;
    }
    for (let i = n; i < this.lastShown; i++) {
      const p = this.particles[i]!;
      p.scaleX = 0;
      p.scaleY = 0;
      p.alpha = 0;
    }
    this.lastShown = n;
  }
}
